import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { loginUser } from './store/slices/userSlice';

import axios from './lib/axios';
import Logout from './views/pages/logout/Logout';
import Loading from './components/Loading';
import './scss/style.scss';
import ToastProvider from './contexts/ToastProvider';

import AdminUserRightsProvider from './contexts/AdminUserRightsProvider';
import { PanelProvider, usePanelContext } from './contexts/PanelProvider';
import MaintenancePage from './views/pages/maintenance/MaintenancePage';
import useClosingPanel from './lib/hooks/useClosingPanel';
import Panel from './components/panel/Panel';
import GoogleReconnect from './views/pages/login/GoogleReconnect';
import PhoneConfirmation from './views/pages/phone_confirmation/PhoneConfirmation';
import { some } from 'lodash';
import axiosInstance from './lib/axios';

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));

// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'));
const RedirectGoogleAuth = React.lazy(() => import('./views/pages/login/RedirectGoogleAuth'));

const Page404 = React.lazy(() => import('./views/pages/page404/Page404'));
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'));
const Onboarding = React.lazy(() => import('./views/pages/onboarding/Onboarding.jsx'));

const client = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

const AppContainer = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userInformations = useSelector((state) => state.user.informations);

    const { onClosingPanel } = useClosingPanel();
    const { panelOpened } = usePanelContext();
    const closePanel = () => {
        if (panelOpened) {
            onClosingPanel();
        }
    };

    const [loading, setLoading] = useState(true);
    const [maintenanceUntil, setMaintenanceUntil] = useState(null);

    const checkAuthenticationAndOnboardingStep = useCallback(() => {
        if (userInformations.loggedIn) {
            // if (userInformations.onboardingStep !== 6 && window.location.pathname !== '/onboarding') {
            //     navigate('/onboarding');
            //     return;
            // }
            setLoading(false);
            return;
        }   

        axiosInstance
            .get('/me')
            .then((response) => {
                dispatch(loginUser(response.data));
                if (window.location.pathname === '/login') {
                    navigate('/dashboard');
                }
            })
            .catch((error) => {
                if (error.response.status === 401) {
                    navigate('login');
                } else if (error.response.status === 503) {
                    setMaintenanceUntil(error.response?.data?.maintenance_until);
                } else {
                    navigate('login');
                    console.warn('An error as occurred');
                }
            })
            .finally(() => setLoading(false));
    }, [dispatch, navigate, userInformations]);

    useEffect(() => {
        if (!some([/\/phone_number_validation\/.*/, /\/google_connect/], (e) => e.test(window.location.pathname))) {
            checkAuthenticationAndOnboardingStep();
        } else {
            setLoading(false);
        }
    }, [dispatch, navigate, checkAuthenticationAndOnboardingStep]);

    if (loading) {
        return <Loading />;
    }

    if (maintenanceUntil) {
        return <MaintenancePage until={maintenanceUntil} />;
    }

    return (
        <>
            <div onClick={closePanel}>
                <Routes>
                    <Route exact path="/login" name="Login Page" element={<Login />} />
                    <Route exact path="/google_connect" name="Google connect" element={<RedirectGoogleAuth />} />
                    <Route exact path="/logout" name="Logout" element={<Logout />} />
                    <Route exact path="/404" name="Page 404" element={<Page404 />} />
                    <Route exact path="/500" name="Page 500" element={<Page500 />} />
                    <Route path="/onboarding/*" name="Onboarding" element={<Onboarding />} />
                    <Route path="*" name="Home" element={<DefaultLayout />} />
                    <Route
                        path="/phone_number_validation/:challenge"
                        name="Phone number validation"
                        element={<PhoneConfirmation />}
                    />
                    <Route
                        path="/link_google_account/:company_id"
                        name="Link Google account"
                        element={<PhoneConfirmation />}
                    />
                </Routes>
            </div>
            <Panel />
        </>
    );
};

const App = () => (
    <BrowserRouter>
        <Suspense fallback={<Loading />}>
            <QueryClientProvider client={client}>
                <ToastProvider>
                    <AdminUserRightsProvider>
                        <PanelProvider>
                            <AppContainer />
                        </PanelProvider>
                    </AdminUserRightsProvider>
                </ToastProvider>
            </QueryClientProvider>
        </Suspense>
    </BrowserRouter>
);

export default App;
